import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "@components/cards/Card/Card";
import TextInput from "@components/inputs/TextInput/TextInput";
import styles from "@styles/CommonEditorPageLayout.module.css";
import { isRequiredField, isValidEmail, isValidMaxLength } from "@util/validators";
import UnsavedChangesPrompt from "@components/misc/UnsavedChangesPrompt";
import EditorCompleteButtons from "@components/buttons/EditorCompleteButtons/EditorCompleteButtons";
import LoadingSpinner from "@components/indicators/LoadingSpinner/LoadingSpinner";
import { MAX_DIRECTOR_NAME_LENGTH } from "@constants/validation";
import { createDirector, fetchDirector, updateDirector } from "@services/directorService";
import { DirectorUpdate } from "@models/Director";
import { delay } from "@util/time";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";

const DirectorEditorPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get("mode");
    const id = searchParams.get("id");

    const [loading, setLoading] = useState(mode === "edit");
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [changedFields, setChangedFields] = useState<DirectorUpdate>({});

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");

    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");

    const hasUnsavedChanges = Object.keys(changedFields).length > 0;

    // Load Director information in edit mode
    useEffect(() => {
        if (mode === "edit") {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    if (!id) {
                        throw new Error("No ID.");
                    }
                    const data = await fetchDirector(id);
                    setFirstName(data.firstName);
                    setLastName(data.lastName);
                    setEmail(data.email);
                } catch (err) {
                    setError("Failed to fetch user information.");
                } finally {
                    setLoading(false);
                }
            };
            void fetchData();
        }
    }, [mode, id]);

    // Validation functions
    const validateFirstName = useCallback((val: string) => {
        const result = isRequiredField(val, "First name");
        if (result) return result;
        return isValidMaxLength(val, MAX_DIRECTOR_NAME_LENGTH);
    }, []);

    const validateLastName = useCallback((val: string) => {
        const result = isRequiredField(val, "Last name");
        if (result) return result;
        return isValidMaxLength(val, MAX_DIRECTOR_NAME_LENGTH);
    }, []);

    const validateEmail = useCallback((val: string) => {
        return isValidEmail(val);
    }, []);

    // Handlers with validation
    const onFirstNameChange = useCallback((val: string) => {
        setFirstName(val);
        setFirstNameError(validateFirstName(val));
        setChangedFields((prev) => ({ ...prev, firstName: val }));
    }, [validateFirstName]);

    const onLastNameChange = useCallback((val: string) => {
        setLastName(val);
        setLastNameError(validateLastName(val));
        setChangedFields((prev) => ({ ...prev, lastName: val }));
    }, [validateLastName]);

    const onEmailChange = useCallback((val: string) => {
        setEmail(val);
        setEmailError(validateEmail(val));
        setChangedFields((prev) => ({ ...prev, email: val }));
    }, [validateEmail]);

    // Save handler
    const handleSave = async () => {
        let isValid = true;

        // Validate First Name
        const firstNameValidationError = validateFirstName(firstName);
        setFirstNameError(firstNameValidationError);
        if (firstNameValidationError) isValid = false;

        // Validate Last Name
        const lastNameValidationError = validateLastName(lastName);
        setLastNameError(lastNameValidationError);
        if (lastNameValidationError) isValid = false;

        // Validate Email
        const emailValidationError = validateEmail(email);
        setEmailError(emailValidationError);
        if (emailValidationError) isValid = false;

        if (isValid) {
            try {
                setIsSaving(true);
                setSubmitErrorMessage("");
                let createdId = "";

                if (mode === "add") {
                    createdId = await createDirector(changedFields);
                } else {
                    if (!id) {
                        throw new Error("No ID.");
                    }
                    await updateDirector(id, changedFields);
                }

                setChangedFields({});
                setIsSaving(false);
                await delay(1); // Wait for saving state to be cleared
                if (mode === "add") {
                    // Change to edit mode after creation
                    searchParams.set("mode", "edit");
                    searchParams.set("id", createdId);
                    navigate("../invite-success", { replace: true });
                }
            } catch (err) {
                const msg = "Failed to save director information. Please try again.";
                setSubmitErrorMessage(msg);
                alert(msg);
            } finally {
                setIsSaving(false);
            }
        }
    };

    const renderContent = () => {
        if (loading || isSaving || error) {
            return <LoadSaveErrorDisplay isLoading={loading} isSaving={isSaving} error={error} />;
        }

        return (
            <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <h1>
                        {mode === "add" ? "Add" : "Edit"}
                        {" "}
                        Company User
                    </h1>
                </div>
                <div className={styles.buttonGroup}>
                    <EditorCompleteButtons
                        hasUnsavedChanges={hasUnsavedChanges}
                        isSaving={isSaving}
                        isNew={mode === "add"}
                        onSave={handleSave}
                    />
                </div>
                <div className={styles.contentContainer}>
                    <Card className={styles.cardContainer}>
                        <TextInput
                            label="First Name *"
                            id="firstName"
                            value={firstName}
                            onChange={onFirstNameChange}
                            placeholder="Enter first name..."
                            errorMessage={firstNameError}
                        />
                        <TextInput
                            label="Last Name *"
                            id="lastName"
                            value={lastName}
                            onChange={onLastNameChange}
                            placeholder="Enter last name..."
                            errorMessage={lastNameError}
                        />
                        <TextInput
                            label="Email *"
                            id="email"
                            value={email}
                            onChange={onEmailChange}
                            placeholder="Enter email..."
                            errorMessage={emailError}
                            disabled={mode === "edit"}
                        />
                        {mode === "add" && (
                            <div className={styles.note}>
                                <p>This user should only be an employee of your company.</p>
                                <p style={{ marginTop: "6px" }}>They will have access to manage services and memorials.</p>
                            </div>
                        )}
                        {submitErrorMessage && (
                            <p className={styles.errorMessage}>{submitErrorMessage}</p>
                        )}
                    </Card>
                </div>
                <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />
            </div>
        );
    };

    return renderContent();
};

export default DirectorEditorPage;
